(() => {
  if (customElements.get('sunpura-power-context')) return;
  customElements.define('sunpura-power-context', class extends HTMLElement {
    connectedCallback() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.init());
    }
    init() {
      if (!this.isConnected) return;
      this.cleanup();
      this.selector = this.querySelector('[data-power-selector]');
      this.tabs = [...this.querySelectorAll('[data-power-tab]')];
      this.panels = [...this.querySelectorAll('[data-power-panel]')];
      if (!this.tabs.length || this.tabs.length !== this.panels.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.selector.setAttribute('role', 'tablist');
      this.selector.setAttribute('aria-orientation', 'horizontal');
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        this.panels[index].setAttribute('role', 'tabpanel');
        this.panels[index].setAttribute('aria-labelledby', tab.id);
        tab.addEventListener('click', () => this.select(index), options);
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index + this.tabs.length - 1) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.select(next);
          this.tabs[next].focus({ preventScroll: true });
        }, options);
      });
      this.addEventListener('shopify:block:select', event => {
        const index = this.panels.findIndex(panel => panel.contains(event.target) ||
          (event.detail?.blockId && panel.querySelector('[data-power-block="' + CSS.escape(event.detail.blockId) + '"]')));
        if (index >= 0) this.select(index, false);
      }, options);
      this.reduced.addEventListener('change', () => {
        if (this.reduced.matches) this.panels.forEach(panel => panel.getAnimations().forEach(animation => animation.cancel()));
      }, options);
      const initial = this.tabs.findIndex(tab => tab.dataset.powerTab === 'ac');
      this.select(initial >= 0 ? initial : 0, false);
      this.setAttribute('data-enhanced', '');
      this.querySelector('[data-power-switch]').hidden = false;
      this.moveIndicator();
      this.observer = new ResizeObserver(() => this.moveIndicator());
      this.observer.observe(this.selector);
      this.tabs.forEach(tab => this.observer.observe(tab));
    }
    select(index, animate = true) {
      if (!this.tabs[index]) return;
      this.active = index;
      this.moveIndicator();
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        const panel = this.panels[i];
        panel.setAttribute('aria-hidden', String(!active));
        panel.inert = !active;
        panel.tabIndex = active ? 0 : -1;
        panel.getAnimations().forEach(animation => animation.cancel());
      });
      if (animate && this.dataset.motion === 'true' && !this.reduced.matches) {
        this.panels[index].animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 300, easing: 'cubic-bezier(.16,1,.3,1)' });
      }
    }
    moveIndicator() {
      const tab = this.tabs?.[this.activeIndex ?? this.active ?? 0];
      if (!tab || !this.selector) return;
      this.selector.style.setProperty('--sp-tab-x', tab.offsetLeft + 'px');
      this.selector.style.setProperty('--sp-tab-width', tab.offsetWidth + 'px');
    }
    cleanup() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.querySelectorAll('[data-power-panel]').forEach(panel => {
        panel.getAnimations().forEach(animation => animation.cancel());
        panel.removeAttribute('role');
        panel.removeAttribute('aria-hidden');
        panel.removeAttribute('tabindex');
        panel.setAttribute('aria-labelledby', panel.id + '-heading');
        panel.inert = false;
      });
      this.removeAttribute('data-enhanced');
      const selector = this.querySelector('[data-power-switch]');
      if (selector) selector.hidden = true;
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.cleanup();
    }
  });
})();
